// CS 응대 메모 (GiftCsMemo) — append-only, 스태프(Admin·GiftAgent) 전용.
// 스펙: docs/gift-cs-memo-spec.md
//
// 주의 1) GiftCoupon.memo(구매자가 수령자에 대해 남기는 메모)와 전혀 다른 것.
//        이쪽은 "어떤 문의를 받아 어떻게 처리했는지" 응대 기록이다.
// 주의 2) 구매자·수령자·상조 관리자에게는 백엔드가 populate에서 강제로 제거한다.
//        유저용 화면에서는 절대 렌더하지 말 것.

export type GiftCsMemoByRole = 'Admin' | 'GiftAgent';

export interface GiftCsMemo {
  _id: string;
  /** 쿠폰/배송 단위 메모에도 항상 스탬프된다 (주문 축 통합 조회 키) */
  giftOrder?: string;
  /** 쿠폰 단위 메모일 때만 */
  giftCoupon?: string;
  /** 배송 단위 메모일 때만 */
  giftDelivery?: string;

  by: string;
  /** 작성자 이름 스냅샷 — populate 없이 바로 렌더 */
  byName: string;
  byRole: GiftCsMemoByRole;

  /** true = Admin 전용 (GiftAgent 비노출). Admin만 세팅 가능 */
  adminOnly?: boolean;

  body: string;
  // axios 응답 인터셉터가 ISO 문자열을 Date로 바꿔 내려준다 (docs 메모리: axios 날짜 자동 변환)
  createdAt: Date;
  updatedAt?: Date;
}

/** 셋 중 정확히 하나만 채운다 */
export interface GiftCsMemoTarget {
  giftOrder?: string;
  giftCoupon?: string;
  giftDelivery?: string;
}

export interface GiftCsMemoCreateDto extends GiftCsMemoTarget {
  body: string;
  adminOnly?: boolean;
}

export type GiftCsMemoAxis = 'giftOrder' | 'giftCoupon' | 'giftDelivery';

// ─── CS 응대 로그 (전체 목록 페이지) ───
// 메모 하나가 어떤 건이었는지 표에서 바로 읽히도록 백엔드가 대상 요약을 붙여준다.
// 스펙: docs/gift-cs-memo-spec.md §3.5

export type GiftCsMemoLogTargetFilter = 'all' | 'order' | 'coupon' | 'delivery';

export interface GiftCsMemoLogItem extends GiftCsMemo {
  /** 주문 요약 — 메모 대상이 무엇이든 항상 (주문 미연결 레거시만 없음) */
  order?: {
    _id: string;
    receiverName?: string;
    receiverPhoneNumber?: string;
    purchasedAt?: Date;
  };
  /** 쿠폰 단위 메모일 때만 */
  coupon?: {
    _id: string;
    code?: string;
    seq?: number;
    fullCode?: string;
    itemName?: string;
    redeemStatus?: string;
  };
  /** 배송 단위 메모일 때만 */
  delivery?: {
    _id: string;
    code?: string;
    deliveryStatus?: string;
  };
}

export interface GiftCsMemoLogParams {
  /** YYYY-MM-DD (KST 하루 경계, createdAt 기준) */
  from?: string;
  to?: string;
  target?: GiftCsMemoLogTargetFilter;
  /** 본문·작성자명·쿠폰코드·주문자명 부분일치 */
  q?: string;
  /** Admin 전용 메모만 보기 (Admin만 의미 있음) */
  adminOnly?: boolean;
  page?: number;
  limit?: number;
}

export interface GiftCsMemoLogResponse {
  items: GiftCsMemoLogItem[];
  total: number;
  page: number;
  limit: number;
}

/** 타깃 객체에서 실제로 채워진 축과 id를 뽑는다 (없으면 null) */
export const resolveCsMemoTarget = (
  target: GiftCsMemoTarget,
): { axis: GiftCsMemoAxis; id: string } | null => {
  if (target.giftCoupon) return { axis: 'giftCoupon', id: target.giftCoupon };
  if (target.giftDelivery) return { axis: 'giftDelivery', id: target.giftDelivery };
  if (target.giftOrder) return { axis: 'giftOrder', id: target.giftOrder };
  return null;
};
